import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useSpreadStore } from 'store/spreadStore';

import { book as bookRoute, login, productionUrl } from '@constants/routes';
import CustomHead from '@elements/CustomHead';
import Spinner from '@elements/Spinner';
import { useBookContext } from '@hooks/useBookContext';
import useMediaQuery from '@hooks/useMediaQuery';
import { isTouchDevice } from '@utils/device';

const BookIndex = () => {
  const router = useRouter();
  const {
    query: { bookId, invite },
    replace,
  } = useRouter();
  const {
    book,
    bookLoading: loading,
    bookError: error,
    userPage,
  } = useBookContext();
  const { isTablet } = useMediaQuery();
  const isMobileEditor = isTouchDevice() && isTablet;
  const { setActiveSpreadSide } = useSpreadStore();

  const metaTitle = invite
    ? 'Doe je mee?'
    : 'Belmondofoto.nl - Samen fotoboeken maken';
  const metaDescription = invite
    ? 'We maken samen een fotoboek bij Belmondofoto.nl.'
    : 'Met Belmondofoto maak je alleen of samen, eenvoudig je eigen fotoboek als cadeau voor je vriend(in), je familie of je collega. Zo leg  je in een handomdraai blijvende herinneringen vast.';

  const head = (
    <CustomHead
      title={metaTitle}
      description={metaDescription}
      canonicalUrl={`${productionUrl}${bookRoute(bookId as string, '')}`}
    >
      <meta name="robots" content="noindex" />
    </CustomHead>
  );

  useEffect(() => {
    if (loading || !book) {
      return;
    }

    if (!bookId) {
      router.push('/404');
      return;
    }

    if (error) {
      if (error.message === 'FORBIDDEN') {
        router.push({
          pathname: '/',
          hash: login,
          query: { redirect: router.asPath },
        });
      } else {
        router.push('/404');
      }
      return;
    }

    // if the user has a spread in this book redirect them to their spread
    if (userPage) {
      if (isMobileEditor) setActiveSpreadSide('left');
      replace({
        pathname: bookRoute(bookId as string, userPage?.id),
        query: isMobileEditor ? { page: 'left' } : {},
      });
      // otherwise, if there is an invite param - redirect them to the first spread with the invite param
    } else if (invite) {
      if (isMobileEditor) setActiveSpreadSide('right');
      replace({
        pathname: bookRoute(bookId as string, book?.spreads[0]?.id),
        query: isMobileEditor ? { page: 'right', invite } : { invite },
      });
    } else {
      if (isMobileEditor) setActiveSpreadSide('right');
      replace({
        pathname: bookRoute(bookId as string, book?.spreads[0]?.id),
        query: isMobileEditor ? { page: 'right' } : {},
      });
    }
  }, [
    loading,
    book,
    bookId,
    error,
    userPage,
    invite,
    isMobileEditor,
    replace,
    router,
    setActiveSpreadSide,
  ]);

  if (loading || !book) {
    return (
      <>
        {head}
        <Spinner />;
      </>
    );
  }

  return head;
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  // Move redirect to server side, so we can use the router.replace method
  // Than you can also remove the bookProver from _app.tsx
  return { props: {} };
};

export default BookIndex;
